// Use a 2x backing store on low-density screens for clearer canvas text.
// CSS sizes stay intact; existing zoom density and pixel-budget limits remain.
export function chartRasterDensity(nativeDensity, pageScale, width, height) {
  const desired = Math.max(2, nativeDensity * Math.max(1, pageScale));
  const budget = Math.max(nativeDensity, Math.min(
    8192 / width, 8192 / height, Math.sqrt(16000000 / (width * height)),
  ));
  return Math.min(desired, budget);
}

export function watchChartDensity(root, theme) {
  // Keep amCharts' reduced resolution on memory-limited devices (e.g. iOS).
  // This integration must not override an existing safe-resolution baseline.
  if (root._renderer.resolution !== (window.devicePixelRatio || 1)) return () => {};
  const viewport = window.visualViewport;
  const graphics = theme.rule('Graphics');
  let nativeDensity = window.devicePixelRatio || 1;
  let rasterDensity = root._renderer.resolution;
  let mediaQuery;
  let pendingFrame;
  let disposed = false;
  let layoutPaused = document.documentElement.classList.contains('chart-layout-transition');

  // amCharts divides crisp stroke widths by renderer.resolution. Compensate
  // only the extra raster density, preserving the original CSS stroke width,
  // opacity and crisp pixel alignment (including newly added indicators).
  const strokeAdapter = graphics.adapters.add('strokeWidth', (width, target) => (
    target.get('crisp') ? (width ?? 1) * rasterDensity / nativeDensity : width
  ));

  const watchDisplay = () => {
    mediaQuery?.removeEventListener('change', schedule);
    mediaQuery = window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`);
    mediaQuery.addEventListener('change', schedule);
  };

  const refresh = () => {
    pendingFrame = undefined;
    if (disposed || root.isDisposed() || layoutPaused) return;
    const { width, height } = root.dom.getBoundingClientRect();
    if (width <= 0 || height <= 0) return;
    const nextNative = window.devicePixelRatio || 1;
    const nextRaster = chartRasterDensity(nextNative, viewport?.scale || 1, width, height);
    const displayChanged = nativeDensity !== nextNative;
    if (displayChanged) watchDisplay();
    if (!displayChanged && rasterDensity === nextRaster && root._renderer.resolution === nextRaster) return;

    nativeDensity = nextNative;
    rasterDensity = nextRaster;
    // amCharts 5.20 has no public runtime resolution setter. Keep this internal
    // integration together: resize backing stores AND rebuild cached strokes.
    root._renderer.resolution = rasterDensity;
    graphics.entities.forEach(entity => {
      if (!entity.isDisposed() && entity.get('crisp')) entity._markDirtyKey('strokeWidth');
    });
    const skipRenderFrame = root.skipRenderFrame;
    root.skipRenderFrame = true;
    try { root.resize(); }
    finally { root.skipRenderFrame = skipRenderFrame; }
    // Called at framestarted: amCharts now processes dirty graphics and paints
    // once, so it never displays the resized canvas with old stroke commands.
  };

  function schedule() {
    if (disposed || root.isDisposed() || pendingFrame) return;
    pendingFrame = root.events.once('framestarted', refresh);
    root.container.markDirty();
  }
  const pause = () => { layoutPaused = true; };
  const resume = () => { layoutPaused = false; schedule(); };
  const observer = new ResizeObserver(schedule);
  observer.observe(root.dom);
  window.addEventListener('resize', schedule);
  viewport?.addEventListener('resize', schedule);
  window.addEventListener('chart-layout-start', pause);
  window.addEventListener('chart-layout-end', resume);
  watchDisplay();
  schedule();

  return () => {
    disposed = true;
    pendingFrame?.dispose();
    observer.disconnect();
    mediaQuery?.removeEventListener('change', schedule);
    window.removeEventListener('resize', schedule);
    viewport?.removeEventListener('resize', schedule);
    window.removeEventListener('chart-layout-start', pause);
    window.removeEventListener('chart-layout-end', resume);
    strokeAdapter.dispose();
  };
}
